// Inline SVG icons (24x24 stroke drawings), built with DOM APIs.

import type { Stage } from '../src/types';

const SVG_NS = 'http://www.w3.org/2000/svg';

const ICONS = {
  // 1 Information leak: an open eye.
  leak: ['M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z', 'M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z'],
  // 2 Policy bypass: an arrow arcing over a barrier.
  bypass: ['M12 11v10', 'M4 19c0-9 16-9 16 0', 'M17 16l3 3 3-3'],
  // 3 Access escalation: a key.
  access: ['M11 15a4 4 0 1 1-8 0 4 4 0 0 1 8 0z', 'M10 12l10-10', 'M16 6l3 3', 'M13.5 8.5l2 2'],
  // 4 Audit suppression: a log with a strike-through.
  audit: ['M6 6h13', 'M6 12h13', 'M6 18h8', 'M3 3l18 18'],
  // 5 Goal adoption: a target.
  goal: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0z', 'M13 12a1 1 0 1 1-2 0 1 1 0 0 1 2 0z'],
  // 6 Delegation of control: a steering wheel.
  control: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'M14.5 12a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z', 'M3 12h6.5', 'M14.5 12H21', 'M12 14.5V21'],
  // Detection: a shield with a check mark.
  detected: ['M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z', 'M8.5 12l2.5 2.5 4.5-5'],
  // A tool call: a terminal prompt.
  tool: ['M4 7l5 5-5 5', 'M12 17h8'],
  // Private controller note: a padlock.
  lock: ['M6 11h12v10H6z', 'M8 11V8a4 4 0 0 1 8 0v3'],
  // External/raw link: a box with an arrow.
  raw: ['M14 4h6v6', 'M20 4l-9 9', 'M18 14v6H4V6h6'],
  // Swap two things.
  swap: ['M7 4l-3 3 3 3', 'M4 7h16', 'M17 20l3-3-3-3', 'M20 17H4'],
} as const;

export type IconName = keyof typeof ICONS;

const STAGE_ICONS: Record<Stage, IconName> = {
  1: 'leak',
  2: 'bypass',
  3: 'access',
  4: 'audit',
  5: 'goal',
  6: 'control',
};

export function icon(name: IconName, label?: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', `icon icon--${name}`);
  if (label) {
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', label);
  } else {
    svg.setAttribute('aria-hidden', 'true');
  }
  for (const d of ICONS[name]) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.append(path);
  }
  return svg;
}

export function stageIcon(stage: Stage, label?: string): SVGSVGElement {
  return icon(STAGE_ICONS[stage], label);
}

function isIconName(v: string): v is IconName {
  return Object.hasOwn(ICONS, v);
}

/** Fills static placeholders: <span data-icon="stage-3"></span>, <span data-icon="detected"></span>. */
export function hydrateIcons(root: ParentNode = document): void {
  for (const el of root.querySelectorAll<HTMLElement>('[data-icon]')) {
    if (el.querySelector('svg')) continue;
    const name = el.dataset.icon ?? '';
    const stage = /^stage-([1-6])$/.exec(name);
    if (stage) el.append(stageIcon(Number(stage[1]) as Stage));
    else if (isIconName(name)) el.append(icon(name));
  }
}
